import { icons, type LucideIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { BLOCK_COLORS } from './tokens';
import { EMOJI_CATEGORIES, randomEmoji, searchEmoji, withSkin, type Emoji } from './emoji';
import { Popover, type Anchor } from './Popover';
import { t as tr } from '@lumen/platform';

const SKIN_TONES = ['✋', '✋🏻', '✋🏼', '✋🏽', '✋🏾', '✋🏿'];
const ICON_NAMES = Object.keys(icons);

export interface IconPickerProps {
  onPick: (icon: string) => void;
  onRemove?: () => void;
  onUpload?: () => void;
  recent?: string[];
  skinTone?: number;
  onSkinTone?: (i: number) => void;
  /** Hide the Icons/Upload tabs (e.g. inline `:` emoji picking). */
  emojiOnly?: boolean;
  initialQuery?: string;
}

export function IconPicker({ onPick, onRemove, onUpload, recent = [], skinTone: skinProp, onSkinTone, emojiOnly, initialQuery = '' }: IconPickerProps) {
  const [tab, setTab] = useState<'emoji' | 'icons' | 'upload'>('emoji');
  const [query, setQuery] = useState(initialQuery);
  const [skinLocal, setSkinLocal] = useState(0);
  const skin = skinProp ?? skinLocal;
  const [skinOpen, setSkinOpen] = useState(false);
  const [iconColor, setIconColor] = useState<string>('default');
  const [category, setCategory] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => (query ? searchEmoji(query, 300) : null), [query]);
  const iconResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (q ? ICON_NAMES.filter((n) => n.toLowerCase().includes(q)) : ICON_NAMES).slice(0, 240);
  }, [query]);

  const emojiButton = (e: Emoji) => (
    <button key={e.id} className="lm-emoji" title={e.name} aria-label={e.name} data-testid={`emoji-${e.id}`} onClick={() => onPick(withSkin(e, skin))}>
      {withSkin(e, skin)}
    </button>
  );

  return (
    <div className="lm-iconpicker" data-testid="icon-picker">
      <div className="lm-iconpicker-tabs">
        <button data-active={tab === 'emoji'} onClick={() => setTab('emoji')} data-testid="icon-tab-emoji">
          {tr('Emoji')}
        </button>
        {!emojiOnly && (
          <>
            <button data-active={tab === 'icons'} onClick={() => setTab('icons')} data-testid="icon-tab-icons">
              {tr('Icons')}
            </button>
            {onUpload && (
              <button data-active={tab === 'upload'} onClick={() => setTab('upload')} data-testid="icon-tab-upload">
                {tr('Upload')}
              </button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        {onRemove && (
          <button onClick={onRemove} data-testid="icon-remove">
            {tr('Remove')}
          </button>
        )}
      </div>
      {tab !== 'upload' && (
        <div className="lm-iconpicker-search">
          <input className="lm-input" autoFocus placeholder={tr('Filter…')} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="icon-search" />
          {tab === 'emoji' && (
            <button
              className="lm-iconpicker-random"
              title={tr('Random')}
              data-testid="icon-random"
              onClick={() => onPick(randomEmoji())}
            >
              🎲
            </button>
          )}
          {tab === 'emoji' && (
            <span className="lm-iconpicker-skin-wrap">
              <button className="lm-iconpicker-random" title={tr('Select skin tone')} data-testid="skin-tone" onClick={() => setSkinOpen((o) => !o)}>
                {SKIN_TONES[skin]}
              </button>
              {skinOpen && (
                <span className="lm-iconpicker-skins" data-testid="skin-tones">
                  {SKIN_TONES.map((t, i) => (
                    <button
                      key={t}
                      data-testid={`skin-tone-${i}`}
                      onClick={() => {
                        setSkinLocal(i);
                        onSkinTone?.(i);
                        setSkinOpen(false);
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </span>
              )}
            </span>
          )}
        </div>
      )}
      {tab === 'emoji' && (
        <>
          <div className="lm-iconpicker-grid" ref={gridRef}>
            {results ? (
              <>
                <div className="lm-iconpicker-cat">{tr('Results')}</div>
                <div className="lm-emoji-grid">{results.map(emojiButton)}</div>
                {results.length === 0 && <div className="lm-menu-empty">{tr('No results')}</div>}
              </>
            ) : (
              <>
                {recent.length > 0 && (
                  <>
                    <div className="lm-iconpicker-cat">{tr('Recent')}</div>
                    <div className="lm-emoji-grid" data-testid="emoji-recent">
                      {recent.slice(0, 18).map((r) => (
                        <button key={r} className="lm-emoji" onClick={() => onPick(r)}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {EMOJI_CATEGORIES.map((c) => (
                  <div key={c.id} data-category={c.id}>
                    <div className="lm-iconpicker-cat">{c.label}</div>
                    <div className="lm-emoji-grid">{c.emojis.map(emojiButton)}</div>
                  </div>
                ))}
              </>
            )}
          </div>
          {!results && (
            <div className="lm-iconpicker-cats">
              {EMOJI_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  title={c.label}
                  data-active={category === c.id}
                  data-testid={`emoji-cat-${c.id}`}
                  onClick={() => {
                    setCategory(c.id);
                    gridRef.current?.querySelector(`[data-category="${c.id}"]`)?.scrollIntoView({ block: 'start' });
                  }}
                >
                  {c.emojis[0]?.native}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {tab === 'icons' && (
        <>
          <div className="lm-iconpicker-colors">
            {['default', ...BLOCK_COLORS].map((c) => (
              <button
                key={c}
                className="lm-iconpicker-color"
                data-active={iconColor === c}
                aria-label={c}
                data-testid={`icon-color-${c}`}
                style={{ background: c === 'default' ? 'var(--lm-color-text-secondary)' : `var(--lm-color-block-text-${c})` }}
                onClick={() => setIconColor(c)}
              />
            ))}
          </div>
          <div className="lm-iconpicker-grid">
            <div className="lm-emoji-grid">
              {iconResults.map((name) => {
                const Cmp = (icons as Record<string, LucideIcon>)[name]!;
                return (
                  <button
                    key={name}
                    className="lm-emoji"
                    title={name}
                    data-testid={`lucide-${name}`}
                    style={{ color: iconColor === 'default' ? 'var(--lm-color-text-secondary)' : `var(--lm-color-block-text-${iconColor})` }}
                    onClick={() => onPick(`lucide:${name}:${iconColor}`)}
                  >
                    <Cmp size={20} />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
      {tab === 'upload' && (
        <div className="lm-iconpicker-upload">
          <button className="lm-btn lm-btn-secondary lm-btn-block" onClick={onUpload} data-testid="icon-upload">
            {tr('Upload an image')}
          </button>
          <p>{tr('Recommended size is 280 × 280 pixels')}</p>
        </div>
      )}
    </div>
  );
}

export function IconPickerPopover({ anchor, onClose, ...props }: IconPickerProps & { anchor: Anchor; onClose: () => void }) {
  return (
    <Popover open={!!anchor} anchor={anchor} onClose={onClose} width={408} maxHeight={390} testId="icon-picker-popover">
      <IconPicker {...props} />
    </Popover>
  );
}
